import React, { useEffect, useMemo, useState } from "react";
import {
  PATTERN_COLOR_COUNTS,
  PATTERN_DENSITIES,
  PATTERN_GARMENT_FORMATS,
  PATTERN_OFFSETS,
  PATTERN_PROCESSES,
  PATTERN_SCALES,
  getFollowUpForSubtype,
  getPatternFollowUpOptions,
  getPatternSubtypes,
  joinMultiValue,
  patternFollowUpIsMultiSelect,
  patternFollowUpNeedsFreeText,
  patternOffsetVisible,
  setFollowUpForSubtype,
  splitMultiValue,
} from "../../constants/imageGeneratorDictionaries";
import {
  ExclusivePills,
  FieldLabel,
  RequiredMark,
  chipClassName,
  fieldLabelClassName,
  helperTextClassName,
  pillOptions,
  sectionClassName,
} from "./ImageGeneratorControls";
import ImageGeneratorEmbroideryType from "./ImageGeneratorEmbroideryType";

type Props = {
  garmentFormat: string;
  onGarmentFormatChange: (value: string) => void;
  patternRepeat: string;
  onPatternRepeatChange: (value: string) => void;
  patternFollowUp: string;
  onPatternFollowUpChange: (value: string) => void;
  patternFollowUpText: string;
  onPatternFollowUpTextChange: (value: string) => void;
  patternOffset: string;
  onPatternOffsetChange: (value: string) => void;
  patternDensity: string;
  onPatternDensityChange: (value: string) => void;
  patternScale: string;
  onPatternScaleChange: (value: string) => void;
  patternColorCount: string;
  onPatternColorCountChange: (value: string) => void;
  patternProcess: string;
  onPatternProcessChange: (value: string) => void;
  embroideryType: string;
  onEmbroideryTypeChange: (value: string) => void;
};

const PRESET_COLOR_COUNTS = new Set<string>(PATTERN_COLOR_COUNTS);
const CUSTOM_MIN = 1;
const CUSTOM_MAX = 100;
const CUSTOM_DEFAULT = 5;

const formatColorCount = (count: number) => `${count} Colors`;

const parseColorCount = (value: string): number | null => {
  const match = value.trim().match(/^(\d+)\s*Colors?$/i);
  if (!match) {
    return null;
  }
  const count = Number(match[1]);
  if (!Number.isFinite(count)) {
    return null;
  }
  return Math.min(CUSTOM_MAX, Math.max(CUSTOM_MIN, Math.round(count)));
};

const clampColorCount = (count: number) =>
  Math.min(CUSTOM_MAX, Math.max(CUSTOM_MIN, Math.round(count)));

const isCustomColorCount = (value: string) => {
  const parsed = parseColorCount(value);
  return parsed != null && !PRESET_COLOR_COUNTS.has(formatColorCount(parsed));
};

const PatternColorCountCustom: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const parsed = parseColorCount(value);
  const customActive = isCustomColorCount(value);
  const [draft, setDraft] = useState(String(parsed ?? CUSTOM_DEFAULT));

  useEffect(() => {
    if (parsed != null) {
      setDraft(String(parsed));
    }
  }, [parsed]);

  const commit = (next: number) => {
    const clamped = clampColorCount(next);
    setDraft(String(clamped));
    onChange(formatColorCount(clamped));
  };

  const current = parsed ?? CUSTOM_DEFAULT;

  return (
    <div
      className={`${chipClassName(customActive)} gap-1.5 px-2`}
      role="group"
      aria-label="Custom max color count"
    >
      <button
        type="button"
        aria-label="Decrease color count"
        className="flex h-5 w-5 items-center justify-center rounded-full text-secondary hover:bg-black/5 disabled:opacity-40"
        disabled={customActive && current <= CUSTOM_MIN}
        onClick={() => commit(current - 1)}
      >
        −
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Custom color count"
        value={draft}
        onFocus={() => {
          if (!customActive) {
            commit(current);
          }
        }}
        onChange={event => {
          const raw = event.target.value.replace(/[^\d]/g, "");
          setDraft(raw);
          if (!raw) {
            return;
          }
          const next = Number(raw);
          if (Number.isFinite(next)) {
            commit(next);
          }
        }}
        onBlur={() => {
          if (!draft) {
            commit(CUSTOM_DEFAULT);
            return;
          }
          commit(Number(draft));
        }}
        className="h-5 w-8 border-0 bg-transparent p-0 text-center text-xs font-normal text-secondary outline-none"
      />
      <button
        type="button"
        aria-label="Increase color count"
        className="flex h-5 w-5 items-center justify-center rounded-full text-secondary hover:bg-black/5 disabled:opacity-40"
        disabled={customActive && current >= CUSTOM_MAX}
        onClick={() => commit(current + 1)}
      >
        +
      </button>
    </div>
  );
};

const MultiSelectPills: React.FC<{
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  /** When false, at least one option must remain selected. */
  allowEmpty?: boolean;
}> = ({ options, value, onChange, allowEmpty = true }) => {
  const selected = useMemo(() => new Set(splitMultiValue(value)), [value]);

  return (
    <div className="flex flex-wrap">
      {options.map(option => {
        const isActive = selected.has(option);
        const isMuted = selected.size > 0 && !isActive;
        return (
          <button
            key={option}
            type="button"
            title={option}
            aria-pressed={isActive}
            onClick={() => {
              const next = new Set(selected);
              if (isActive) {
                if (!allowEmpty && selected.size <= 1) {
                  return;
                }
                next.delete(option);
              } else {
                next.add(option);
              }
              onChange(joinMultiValue(options.filter(item => next.has(item))));
            }}
            className={`${chipClassName(isActive, isMuted)} mr-2 mb-2`}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

const SubtypeFollowUpBlock: React.FC<{
  subtype: string;
  showLabel: boolean;
  patternFollowUp: string;
  patternFollowUpText: string;
  patternOffset: string;
  activeSubtypes: readonly string[];
  onPatternFollowUpChange: (value: string) => void;
  onPatternFollowUpTextChange: (value: string) => void;
  onPatternOffsetChange: (value: string) => void;
}> = ({
  subtype,
  showLabel,
  patternFollowUp,
  patternFollowUpText,
  patternOffset,
  activeSubtypes,
  onPatternFollowUpChange,
  onPatternFollowUpTextChange,
  onPatternOffsetChange,
}) => {
  const options = getPatternFollowUpOptions(subtype);
  const followUpValue = getFollowUpForSubtype(
    patternFollowUp,
    subtype,
    activeSubtypes
  );
  const freeTextValue = getFollowUpForSubtype(
    patternFollowUpText,
    subtype,
    activeSubtypes
  );
  const multi = patternFollowUpIsMultiSelect(subtype);
  const showFreeText = patternFollowUpNeedsFreeText(subtype, followUpValue);
  const showOffset = patternOffsetVisible(subtype, followUpValue);

  const updateFollowUp = (next: string) => {
    onPatternFollowUpChange(
      setFollowUpForSubtype(patternFollowUp, subtype, next, activeSubtypes)
    );
    if (subtype === "AOP" && !splitMultiValue(next).includes("Offset")) {
      onPatternOffsetChange("");
    }
    if (!patternFollowUpNeedsFreeText(subtype, next)) {
      onPatternFollowUpTextChange(
        setFollowUpForSubtype(patternFollowUpText, subtype, "", activeSubtypes)
      );
    }
  };

  return (
    <div>
      {showLabel ? <p className={`${fieldLabelClassName}`}>{subtype}</p> : null}
      {subtype === "Panel" ? null : multi ? (
        <MultiSelectPills
          options={options}
          value={followUpValue}
          onChange={updateFollowUp}
        />
      ) : options.length > 0 ? (
        <ExclusivePills
          options={pillOptions(options)}
          value={followUpValue}
          onChange={next => updateFollowUp(next || "")}
          allowDeselect
        />
      ) : null}
      {showFreeText ? (
        <input
          type="text"
          value={freeTextValue}
          onChange={event =>
            onPatternFollowUpTextChange(
              setFollowUpForSubtype(
                patternFollowUpText,
                subtype,
                event.target.value,
                activeSubtypes
              )
            )
          }
          placeholder={
            subtype === "Panel"
              ? "Describe panel placement…"
              : "Describe placement…"
          }
          className="mt-2 w-full max-w-md rounded-md border border-[#d4d4d4] px-3 py-2 text-sm text-secondary outline-none focus:border-secondary"
          aria-label={`${subtype} free text`}
        />
      ) : null}
      {showOffset ? (
        <div className="mt-3">
          <FieldLabel>Offset%</FieldLabel>
          <ExclusivePills
            options={pillOptions(PATTERN_OFFSETS)}
            value={patternOffset}
            onChange={onPatternOffsetChange}
            allowDeselect
          />
        </div>
      ) : null}
    </div>
  );
};

const ImageGeneratorPatternSpecs: React.FC<Props> = ({
  garmentFormat,
  onGarmentFormatChange,
  patternRepeat,
  onPatternRepeatChange,
  patternFollowUp,
  onPatternFollowUpChange,
  patternFollowUpText,
  onPatternFollowUpTextChange,
  patternOffset,
  onPatternOffsetChange,
  patternDensity,
  onPatternDensityChange,
  patternScale,
  onPatternScaleChange,
  patternColorCount,
  onPatternColorCountChange,
  patternProcess,
  onPatternProcessChange,
  embroideryType,
  onEmbroideryTypeChange,
}) => {
  const subtypes = getPatternSubtypes(garmentFormat);

  // Always keep patternRepeat = all subtypes for the current format.
  useEffect(() => {
    if (!garmentFormat) {
      if (patternRepeat) {
        onPatternRepeatChange("");
      }
      return;
    }
    const encoded = joinMultiValue(subtypes);
    if (encoded && encoded !== patternRepeat) {
      onPatternRepeatChange(encoded);
    }
  }, [garmentFormat, patternRepeat, subtypes, onPatternRepeatChange]);

  // Drop follow-up / free-text entries for subtypes no longer in this format.
  useEffect(() => {
    const pruneToActive = (
      encoded: string,
      onChange: (value: string) => void
    ) => {
      const rebuilt = subtypes.reduce((acc, subtype) => {
        const current = getFollowUpForSubtype(encoded, subtype, subtypes);
        return setFollowUpForSubtype(acc, subtype, current, subtypes);
      }, "");
      if (rebuilt !== encoded) {
        onChange(rebuilt);
      }
    };
    pruneToActive(patternFollowUp, onPatternFollowUpChange);
    pruneToActive(patternFollowUpText, onPatternFollowUpTextChange);

    const aopFollowUp = getFollowUpForSubtype(patternFollowUp, "AOP", subtypes);
    if (
      (!subtypes.includes("AOP") ||
        !patternOffsetVisible("AOP", aopFollowUp)) &&
      patternOffset
    ) {
      onPatternOffsetChange("");
    }
  }, [
    subtypes,
    patternFollowUp,
    patternFollowUpText,
    patternOffset,
    onPatternFollowUpChange,
    onPatternFollowUpTextChange,
    onPatternOffsetChange,
  ]);

  const handleFormatChange = (next: string) => {
    if (!next) {
      return;
    }
    onGarmentFormatChange(next);
    onPatternRepeatChange(joinMultiValue(getPatternSubtypes(next)));
    onPatternFollowUpChange("");
    onPatternFollowUpTextChange("");
    onPatternOffsetChange("");
    if (next !== "Print") {
      onPatternProcessChange("");
    }
  };

  return (
    <div className={sectionClassName}>
      <div className="space-y-5">
        <div>
          <p className={`${helperTextClassName} mb-3`}>
            What format would you like the pattern to appear on the garments?
            <RequiredMark />
          </p>
          <ExclusivePills
            options={pillOptions(PATTERN_GARMENT_FORMATS)}
            value={garmentFormat}
            onChange={handleFormatChange}
            allowDeselect={false}
          />
        </div>

        {subtypes.map(subtype => (
          <SubtypeFollowUpBlock
            key={subtype}
            subtype={subtype}
            showLabel
            patternFollowUp={patternFollowUp}
            patternFollowUpText={patternFollowUpText}
            patternOffset={patternOffset}
            activeSubtypes={subtypes}
            onPatternFollowUpChange={onPatternFollowUpChange}
            onPatternFollowUpTextChange={onPatternFollowUpTextChange}
            onPatternOffsetChange={onPatternOffsetChange}
          />
        ))}

        <div>
          <FieldLabel required>Print Density</FieldLabel>
          <ExclusivePills
            options={pillOptions(PATTERN_DENSITIES)}
            value={patternDensity}
            onChange={onPatternDensityChange}
            allowDeselect={false}
          />
        </div>

        <div>
          <FieldLabel required>Scale of the pattern</FieldLabel>
          <ExclusivePills
            options={pillOptions(PATTERN_SCALES)}
            value={patternScale}
            onChange={onPatternScaleChange}
            allowDeselect={false}
          />
        </div>

        {garmentFormat === "Print" ? (
          <div>
            <p className={`${helperTextClassName} mb-3`}>
              Can we get more specific?
              <RequiredMark />
            </p>
            <ExclusivePills
              options={PATTERN_PROCESSES.map(item => ({
                value: item,
                label:
                  item === "Screen"
                    ? "Screen Print"
                    : item === "Digital"
                      ? "Digital Print"
                      : item,
              }))}
              value={patternProcess}
              onChange={onPatternProcessChange}
              allowDeselect={false}
            />
          </div>
        ) : null}

        <div>
          <FieldLabel required>Max color count</FieldLabel>
          <ExclusivePills
            options={pillOptions(PATTERN_COLOR_COUNTS)}
            value={
              PRESET_COLOR_COUNTS.has(patternColorCount)
                ? patternColorCount
                : ""
            }
            onChange={onPatternColorCountChange}
            allowDeselect={false}
            trailing={
              <PatternColorCountCustom
                value={patternColorCount}
                onChange={onPatternColorCountChange}
              />
            }
          />
        </div>

        {garmentFormat === "Embroidery" ? (
          <ImageGeneratorEmbroideryType
            value={embroideryType}
            onChange={onEmbroideryTypeChange}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ImageGeneratorPatternSpecs;
